import { BriefResponseDto } from '../../../../common/general-types/briefs.dto';
import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { take } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { HasBoltOnDirectiveDirective } from '../../../../common/directives/has-bolt-on/has-bolt-on-directive.directive';
import { NotHasBoltOnDirectiveDirective } from '../../../../common/directives/not-has-bolt-on/not-has-bolt-on-directive.directive';
import { ChoosePlanService } from '../../../choose-plan/service/choose-plan/choose-plan.service';
import { SubmittedPostsService } from '../../../../services/submitted-posts/submitted-posts.service';
import { SuperSocialService } from '../../../../services/super-social/super-social.service';
import { VideoService } from '../../../../services/video/video.service';
import { ChatGptService } from '../../../../services/chat-gpt/chat-gpt.service';
import { FormatService } from '../../../../common/format/format.service';
import { SubmittedPostDto } from '../../../../common/general-types/submitted-posts.dto';
import { ContentSummaryDto, LatestPostResultsDto } from '../../../../common/general-types/super-social.dto';
import {
  ContentSummaryAnalysisDto,
  RetroSuggestedBriefResult,
  TopPerformerCommonalitiesResponseDto,
} from '../../../../common/general-types/retro-v2.dto';
import { GptCompletionMessage } from '../../../../common/general-types/open-ai.dto';
import { awarenessGoals, considerationGoals, conversionGoals } from '../../../../common/app-config/layout';
import { LoadingInlineComponent } from '../../../../atoms/loading-inline/loading-inline.component';
import { MediaViewerSimplerComponent } from '../../../../atoms/media-viewer-simpler/media-viewer-simpler.component';
import { BriefAiService } from '../../../create-brief-select/services/brief-ai.service';
import { CreateBriefService } from '../../../briefs/service/create-brief/create-brief.service';
import { BriefService } from '../../../../services/brief/brief.service';

type GoalType = 'awareness' | 'consideration' | 'conversion';
type SuggestedBriefLoadingState = 'idle' | 'loading' | 'loaded' | 'error';

interface TopPerformer {
  videoId: string;
  handle: string;
  profilePic: string;
  metricLabel: string;
  metricDisplay: string;
  coverImage: string;
  post: LatestPostResultsDto;
}

@Component({
  selector: 'mbt-retro-v2',
  imports: [HasBoltOnDirectiveDirective, NotHasBoltOnDirectiveDirective, LoadingInlineComponent, MediaViewerSimplerComponent],
  templateUrl: './retro-v2.html',
  styleUrl: './retro-v2.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RetroV2 implements OnInit {

  environment = environment;

  focussedBrief = input.required<BriefResponseDto>();

  isLoading = signal<boolean>(true);
  noContentForRetro = signal<boolean>(false);
  noCommonalities = signal<boolean>(false);

  topPerformers = signal<TopPerformer[]>([]);
  commonalities = signal<TopPerformerCommonalitiesResponseDto | undefined>(undefined);

  /** Content summary (GPT) — runs independently of the page-level isLoading */
  isLoadingContentSummary = signal<boolean>(true);
  noContentSummary = signal<boolean>(false);
  contentSummary = signal<ContentSummaryAnalysisDto | undefined>(undefined);

  /** Suggested next brief (GPT) — runs after commonalities + content summary settle */
  suggestedBriefLoadingState = signal<SuggestedBriefLoadingState>('idle');
  suggestedBrief = signal<RetroSuggestedBriefResult | undefined>(undefined);
  suggestedBriefError = signal<string | undefined>(undefined);

  private commonalitiesSettled = signal(false);
  private contentSummarySettled = signal(false);
  private suggestedBriefGenerationStarted = false;

  /** Services */
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private choosePlanService = inject(ChoosePlanService);
  private submittedPostsService = inject(SubmittedPostsService);
  private superSocialService = inject(SuperSocialService);
  private videoService = inject(VideoService);
  private chatGptService = inject(ChatGptService);
  private briefAiService = inject(BriefAiService);
  private createBriefService = inject(CreateBriefService);
  private briefService = inject(BriefService);
  public formatService = inject(FormatService);
  /** Services - END */

  private readonly contentSummarySystemMessage: GptCompletionMessage = {
    role: 'system',
    content: `You are a machine that only returns and replies with valid, iterable RFC8259 compliant JSON in your responses. Your text and punctuation should be compliant for white-space: pre-line.
     You are a social media expert helping a brand come up with a brief to instruct content creators.
     I've gathered a data set that summarises key facts about the content of each video.
     Use the key facts to identify commonality and provide a basis for creating a brief.`,
  };

  ngOnInit(): void {
    this.loadRetro();
  }

  private loadRetro(): void {
    const brief = this.focussedBrief();
    if (!brief?.shoutBrief?.id) {
      this.isLoading.set(false);
      this.noContentForRetro.set(true);
      return;
    }

    this.submittedPostsService
      .getValidSubmittedPostsForShoutBriefId(brief.shoutBrief.id, undefined)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (submittedPosts: SubmittedPostDto[]) => {
          console.log('loadRetro() -> submittedPosts: ', submittedPosts);
          if (!submittedPosts?.length) {
            this.isLoading.set(false);
            this.noContentForRetro.set(true);
            return;
          }
          this.loadSuperSocialResults(submittedPosts);
        },
        error: () => {
          this.isLoading.set(false);
          this.noContentForRetro.set(true);
        },
      });
  }

  private loadSuperSocialResults(submittedPosts: SubmittedPostDto[]): void {
    const postIds = submittedPosts
      .map((post) => post.ss_post_id)
      .filter((postId): postId is string => !!postId);

    if (!postIds.length) {
      this.isLoading.set(false);
      this.noContentForRetro.set(true);
      return;
    }

    this.superSocialService
      .getCompleteLatestResultsForPosts(postIds)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: { results: Record<string, LatestPostResultsDto>; submittedPosts: SubmittedPostDto[] }) => {
          const results = res?.results ? Object.values(res.results) : [];
          console.log('loadSuperSocialResults() -> results: ', results);
          if (!results.length) {
            this.isLoading.set(false);
            this.noContentForRetro.set(true);
            return;
          }
          const performers = this.buildTopPerformers(results);
          console.log('loadSuperSocialResults() -> performers: ', performers);
          this.topPerformers.set(performers);
          this.loadCommonalities(performers);
          this.loadContentSummary(performers);
        },
        error: () => {
          this.isLoading.set(false);
          this.noContentForRetro.set(true);
        },
      });
  }

  private buildTopPerformers(results: LatestPostResultsDto[]): TopPerformer[] {
    const goalType = this.resolveGoalType();

    const sorted = [...results].sort((a, b) => this.metricValue(b, goalType) - this.metricValue(a, goalType));

    return sorted.slice(0, 5).map((result) => ({
      videoId: result.submittedPost?.videoId ?? '',
      handle: result.handle,
      profilePic: result.submittedPost?.creator?.photoURL ?? '',
      metricLabel: this.metricLabel(goalType),
      metricDisplay: this.metricDisplay(result, goalType),
      coverImage: this.getCoverImage(result),
      post: result,
    }));
  }

  private loadCommonalities(performers: TopPerformer[]): void {
    console.log('loadCommonalities() -> performers: ', performers);
    const videoIds = [...new Set(performers.map((p) => p.videoId).filter((id) => !!id))];

    if (!videoIds.length) {
      this.isLoading.set(false);
      this.noCommonalities.set(true);
      this.markCommonalitiesSettled();
      return;
    }

    this.videoService
      .getTopPerformerCommonalities(videoIds)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: TopPerformerCommonalitiesResponseDto) => {
          console.log('loadCommonalities() -> res: ', res);
          this.commonalities.set(res);
          this.noCommonalities.set(
            !res || (res.approvedCommonalities.length === 0 && res.issueCommonalities.length === 0),
          );
          this.isLoading.set(false);
          this.markCommonalitiesSettled();
        },
        error: () => {
          this.noCommonalities.set(true);
          this.isLoading.set(false);
          this.markCommonalitiesSettled();
        },
      });
  }

  /** Content summary (GPT) */
  private loadContentSummary(performers: TopPerformer[]): void {
    const contentSummaries = performers
      .map((performer) => performer.post?.contentSummary)
      .filter((summary): summary is ContentSummaryDto => !!summary);

    if (!contentSummaries.length) {
      this.noContentSummary.set(true);
      this.isLoadingContentSummary.set(false);
      this.markContentSummarySettled();
      return;
    }

    const amalgamated = {
      actions: [] as string[],
      brandsMentioned: [] as string[],
      contentType: [] as string[],
      keywordsByIndustry: [] as string[],
      productsShown: [] as string[],
      overallTone: [] as string[],
    };

    contentSummaries.forEach((summary) => {
      amalgamated.actions.push(...(summary.actions ?? []));
      amalgamated.brandsMentioned.push(...(summary.brandsMentioned ?? []));
      amalgamated.contentType.push(summary.contentType ?? '');
      amalgamated.keywordsByIndustry.push(...(summary.keywordsByIndustry ?? []));
      amalgamated.productsShown.push(...(summary.productsShown ?? []));
      amalgamated.overallTone.push(summary.overallTone ?? '');
    });

    amalgamated.actions = [...new Set(amalgamated.actions)];
    amalgamated.brandsMentioned = [...new Set(amalgamated.brandsMentioned)];

    const messages: GptCompletionMessage[] = [
      this.contentSummarySystemMessage,
      {
        role: 'user',
        content: `${this.buildContentSummaryPrompt()}
        Here is the JSON object containing the extracted information from the social media videos:
        ${JSON.stringify(amalgamated)}
        `,
      },
    ];

    this.chatGptService
      .sendCompletionMessage(messages)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: unknown) => {
          try {
            const { choices } = res as { choices: { message: { content: string } }[] };
            const content = JSON.parse(choices[0].message.content) as {
              hasCommonElements?: boolean;
              actionsSummary?: string;
              overallToneSummary?: string;
              contentTypeSummary?: string;
              keywordsByIndustrySummary?: string;
              productsShownSummary?: string;
            };

            if (content.hasCommonElements === false) {
              this.noContentSummary.set(true);
            } else {
              this.contentSummary.set({
                actionsSummary: content.actionsSummary ?? '',
                overallToneSummary: content.overallToneSummary ?? '',
                contentTypeSummary: content.contentTypeSummary ?? '',
                keywordsByIndustrySummary: content.keywordsByIndustrySummary ?? '',
                productsShownSummary: content.productsShownSummary ?? '',
              });
            }
          } catch {
            this.noContentSummary.set(true);
          }
          this.isLoadingContentSummary.set(false);
          this.markContentSummarySettled();
        },
        error: () => {
          this.noContentSummary.set(true);
          this.isLoadingContentSummary.set(false);
          this.markContentSummarySettled();
        },
      });
  }

  private buildContentSummaryPrompt(): string {
    return `
      You are a senior marketing strategist and content analyst specializing in social media, influencer, and product-led growth content.

      You will be given a JSON object containing extracted information from social media videos, including arrays such as actions, overallTone, contentType, keywordsByIndustry, and productsShown.

      Your task is to:
      - Interpret the data through a marketing and brand storytelling lens.
      - Focus on user benefits, emotional appeal, and lifestyle integration.
      - Synthesize patterns rather than listing individual items.
      - Use concise, persuasive language suitable for brand decks, campaign summaries, or performance reports.
      - Emphasize how the product fits naturally into everyday life and solves real user problems.
      - Avoid technical jargon unless it clearly supports a marketing insight.
      - Do NOT invent information not supported by the input.
      - Do NOT reference the input arrays directly.

      Return your response as **valid JSON only**, with the following fixed keys:

      - "hasCommonElements" (boolean) — set to false when the videos do not share meaningful common traits across the categories below; when false, the summary strings may be empty
      - "actionsSummary"
      - "overallToneSummary"
      - "contentTypeSummary"
      - "keywordsByIndustrySummary"
      - "productsShownSummary"

      Each summary value should be a short, marketing-led paragraph (1–2 sentences max).

      Do not include markdown, explanations, commentary, or additional keys.
      `;
  }

  /** Goal → metric helpers */
  private resolveGoalType(): GoalType {
    const goalName = this.focussedBrief().brief_goal_name;
    if (considerationGoals.some((goal) => goal.name === goalName)) {
      return 'consideration';
    }
    if (conversionGoals.some((goal) => goal.name === goalName)) {
      return 'conversion';
    }
    return 'awareness';
  }

  private metricValue(result: LatestPostResultsDto, goalType: GoalType): number {
    if (goalType === 'consideration') {
      return Number(result.views) || 0;
    }
    if (goalType === 'conversion') {
      return Number(result.likes) || 0;
    }
    return Number(result.engagementRate) || 0;
  }

  private metricLabel(goalType: GoalType): string {
    if (goalType === 'consideration') {
      return 'Views';
    }
    if (goalType === 'conversion') {
      return 'Likes';
    }
    return 'Engagement Rate';
  }

  private metricDisplay(result: LatestPostResultsDto, goalType: GoalType): string {
    if (goalType === 'consideration') {
      return `${this.formatService.convertIntToString(Number(result.views) || 0)} views`;
    }
    if (goalType === 'conversion') {
      return `${this.formatService.convertIntToString(Number(result.likes) || 0)} likes`;
    }
    return `${(Number(result.engagementRate) || 0).toFixed(2)}% engagement`;
  }

  getCoverImage(result: LatestPostResultsDto): string {
    const fallback = 'https://ionicframework.com/docs/img/demos/thumbnail.svg';
    const video = result.submittedPost?.video;
    // Prefer the video's real thumbnail URL. Note: `video.thumbnail` is NOT a URL
    // (the backend stores the literal string 'base64 - can save later' there), so
    // only use it if it actually looks like a URL.
    if (video?.thumbnailURL) {
      return video.thumbnailURL;
    }
    if (video?.thumbnail && video.thumbnail.includes('http')) {
      return video.thumbnail;
    }
    if (result.tiktokVideoFile) {
      return result.tiktokVideoFile.cover || fallback;
    }
    if (result.tiktokCarouselFile) {
      return result.tiktokCarouselFile.images?.[0]?.s3_url || fallback;
    }
    if (result.instagramVideoFile) {
      return result.instagramVideoFile.cover || fallback;
    }
    if (result.instagramCarouselFile) {
      return result.instagramCarouselFile.images?.[0]?.s3_url || fallback;
    }
    return fallback;
  }

  openChoosePlan(): void {
    this.choosePlanService.launchSubscribeOrBoltOn(environment.stripe.shaas21products.bo_ai_moderation);
  }

  openSuggestedBriefInBuilder(): void {
    const result = this.suggestedBrief();
    if (!result) {
      return;
    }

    this.createBriefService.seedFromSourceBrief(this.focussedBrief());
    this.briefAiService.applyDraftResult(result.draft);
    this.briefService.launchingFromRetroSuggestedBrief = true;
    this.router.navigate(['/create-brief-select']);
  }

  retrySuggestedBrief(): void {
    this.suggestedBriefGenerationStarted = false;
    this.suggestedBriefLoadingState.set('idle');
    this.suggestedBriefError.set(undefined);
    this.tryGenerateSuggestedBrief();
  }

  private markCommonalitiesSettled(): void {
    this.commonalitiesSettled.set(true);
    this.tryGenerateSuggestedBrief();
  }

  private markContentSummarySettled(): void {
    this.contentSummarySettled.set(true);
    this.tryGenerateSuggestedBrief();
  }

  private tryGenerateSuggestedBrief(): void {
    if (!this.commonalitiesSettled() || !this.contentSummarySettled()) {
      return;
    }

    if (this.suggestedBriefGenerationStarted) {
      return;
    }

    if (this.noCommonalities() && this.noContentSummary()) {
      this.suggestedBriefLoadingState.set('idle');
      return;
    }

    this.suggestedBriefGenerationStarted = true;
    this.suggestedBriefLoadingState.set('loading');
    this.suggestedBriefError.set(undefined);

    const brief = this.focussedBrief();
    const approvedCommonalities = this.commonalities()?.approvedCommonalities ?? [];
    const summary = this.contentSummary();

    void this.briefAiService
      .draftBriefFromRetro({
        sourceBriefId: brief.id,
        sourceBriefTitle: brief.title,
        sourceBriefGoalName: brief.brief_goal_name,
        sourceBriefGoalDescription: brief.brief_goal_description,
        product: brief.product,
        goal: this.resolveBriefGoal(brief),
        approvedCommonalities,
        contentSummary: summary,
      })
      .then((result) => {
        if (result) {
          this.suggestedBrief.set(result);
          this.suggestedBriefLoadingState.set('loaded');
          return;
        }

        this.suggestedBriefLoadingState.set('error');
        this.suggestedBriefError.set(
          this.briefAiService.draftError() ?? 'Could not generate a suggested brief. Please try again.',
        );
      });
  }

  private resolveBriefGoal(
    brief: BriefResponseDto,
  ): { id: number; name: string; description: string; color: string } | undefined {
    const goalName = brief.brief_goal_name ?? brief.brief_goal?.name;
    if (!goalName) {
      return undefined;
    }
    const allGoals = [...awarenessGoals, ...considerationGoals, ...conversionGoals];
    return allGoals.find((goal) => goal.name === goalName);
  }
}
